import React, { useState, useEffect } from "react";
import { PageContainer } from "../components/Container/PageContainer";
import { useTranslation } from "react-i18next";
import { getAllUsers } from "../utils/pureDataFetch";
import { Stack, Grid, TextInput, Select, Flex, Text } from "@mantine/core";
import DataLoader from "../components/DataLoader";
import { Section } from "../components/Container/Section";
import { SectionTitleWithExp } from "../components/customTypo/SectionTitleWithExp";
import { CardUser } from "../components/Card/CardUser";
import { IconSearch, IconUsers } from "@tabler/icons-react";
import { useIsMobile } from "../hooks/useIsMobile";

const ThreaderList = () => {
	const { t } = useTranslation();
	const isMobile = useIsMobile('xl');
	const [users, setUsers] = useState([]);
	const [filteredUsers, setFilteredUsers] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [searchQuery, setSearchQuery] = useState("");
	const [sortBy, setSortBy] = useState("threads");

	useEffect(() => {
		const fetchUsers = async () => {
			try {
				setLoading(true);
				const allUsers = await getAllUsers();
				setUsers(allUsers);
				setFilteredUsers(allUsers);
			} catch (error) {
				console.error("Error fetching users:", error);
				setError("유저 정보를 불러올 수 없습니다.");
			} finally {
				setLoading(false);
			}
		};

		fetchUsers();
	}, []);

	// 검색 및 정렬
	useEffect(() => {
		let filtered = users.filter((user) => {
			const username = user.profile?.username?.toLowerCase() || "";
			const name = user.profile?.name?.toLowerCase() || "";
			const biography = user.profile?.threads_biography?.toLowerCase() || "";
			const query = searchQuery.toLowerCase();
			
			return username.includes(query) || 
				   name.includes(query) || 
				   biography.includes(query);
		});

		// 정렬
		filtered.sort((a, b) => {
			switch (sortBy) {
				case "threads":
					return (b.insight?.list?.numThreads || 0) - (a.insight?.list?.numThreads || 0);
				case "collections":
					return (b.collectionList?.length || 0) - (a.collectionList?.length || 0);
				case "followers":
					return (b.insight?.list?.followers?.value || 0) - (a.insight?.list?.followers?.value || 0);
				case "username":
					return (a.profile?.username || "").localeCompare(b.profile?.username || "");
				default:
					return 0;
			}
		});

		setFilteredUsers(filtered);
	}, [users, searchQuery, sortBy]);

	if (loading) {
		return (
			<PageContainer>
				<DataLoader msg="유저 정보를 불러오는 중..." isLoad={true} />
			</PageContainer>
		);
	}

	if (error) {
		return (
			<PageContainer>
				<Stack align="center" gap={24} pt={100}>
					<Text size="xl" c="red">{error}</Text>
				</Stack>
			</PageContainer>
		);
	}

	return (
		<PageContainer>
				{/* 헤더 섹션 */}
				<Section>
					<SectionTitleWithExp
						isMobile={isMobile}
						title="All Threaders"
						exp={`전체 ${users.length}명의 Threaders들을 만나보세요!`}
					/>
					
					{/* 검색 및 정렬 */}
					<Flex 
						gap={16} 
						direction={isMobile ? "column" : "row"}
						align={isMobile ? "stretch" : "center"}
						mb={32}
					>
						<TextInput
							placeholder="사용자명, 이름, 소개로 검색..."
							leftSection={<IconSearch size={16} />}
							value={searchQuery}
							onChange={(event) => setSearchQuery(event.currentTarget.value)}
							flex={1}
							size={isMobile ? "md" : "lg"}
						/>
						<Select
							placeholder="정렬 기준"
							value={sortBy}
							onChange={setSortBy}
							data={[
								{ value: "threads", label: "스레드 수" },
								{ value: "collections", label: "컬렉션 수" },
								{ value: "followers", label: "팔로워 수" },
								{ value: "username", label: "사용자명" },
							]}
							w={isMobile ? "100%" : 200}
							size={isMobile ? "md" : "lg"}
						/>
					</Flex>
				</Section>

				{/* 유저 카드 그리드 */}
				<Section>
					{filteredUsers.length > 0 ? (
						<Grid>
							{filteredUsers.map((user) => (
								<Grid.Col 
									key={user.id}
									span={isMobile ? 12 : 6} 
									lg={4}
									xl={3}
								>
									<CardUser 
										user={user}
										isMobile={isMobile}
									/>
								</Grid.Col>
							))}
						</Grid>
					) : (
						<Stack align="center" gap={16} py={80}>
							<IconUsers size={48} color="gray" />
							<Text size="lg" c="dimmed">
								검색 결과가 없습니다.
							</Text>
						</Stack>
					)}
				</Section>
		</PageContainer>
	);
};

export default ThreaderList; 